import { XMarkIcon } from '@heroicons/react/24/outline';
import { DragDropContext, Droppable, DropResult } from '@hello-pangea/dnd';
import { T } from '../../../../../../Translations';
import { observer } from 'mobx-react';
import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useLearningCollection } from '../LearningCollectionContext';
import { useExperienceMap } from '../Section/DisplayTypes/Shared/hooks/useExperienceMap';
import { ExperienceResourceArray, ProductCollectionResourceArray } from '../Types';
import DraggableSection from './DraggableSection';
import DrawerWarningAlert from './DrawerWarningAlert';

interface SectionsDrawerProps {
  open: boolean;
  onClose: () => void;
  productCollections: ProductCollectionResourceArray;
  hasUnlicensedPrograms: boolean;
  onViewLicenseDetails: () => void;
  allExperiences: ExperienceResourceArray;
}

// Parse section index from droppableId format: "programs-{sectionIndex}"
function parseProgramDroppableId(droppableId: string): number {
  return parseInt(droppableId.replace('programs-', ''), 10);
}

function SectionsDrawer({
  open,
  onClose,
  productCollections,
  hasUnlicensedPrograms,
  onViewLicenseDetails,
  allExperiences,
}: SectionsDrawerProps) {
  const { reorderProductCollections, reorderProgramsInSection, moveProgramBetweenSections } =
    useLearningCollection();

  const experienceMap = useExperienceMap(allExperiences);

  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());
  const isDraggingRef = useRef(false);

  // Reset drag state on unmount to prevent list page from being unresponsive
  useEffect(() => {
    return () => {
      isDraggingRef.current = false;
    };
  }, []);

  const toggleSection = useCallback((index: number) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const sectionItems = useMemo(
    () =>
      productCollections.map((section, index) => ({
        section,
        index,
        key: section.payload.id.getOrElseValue(index),
        programCount: section.payload.experiences.length,
        isExpanded: expandedSections.has(index),
      })),
    [productCollections, expandedSections],
  );

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;

      const { source, destination, type } = result;

      if (type === 'SECTION') {
        if (source.index !== destination.index) {
          reorderProductCollections(source.index, destination.index);
        }
        return;
      }

      if (type === 'PROGRAM') {
        const sourceSectionIndex = parseProgramDroppableId(source.droppableId);
        const destSectionIndex = parseProgramDroppableId(destination.droppableId);

        if (sourceSectionIndex === destSectionIndex) {
          if (source.index !== destination.index) {
            reorderProgramsInSection(sourceSectionIndex, source.index, destination.index);
          }
        } else {
          moveProgramBetweenSections(
            sourceSectionIndex,
            destSectionIndex,
            source.index,
            destination.index,
          );
        }
      }
    },
    [reorderProductCollections, reorderProgramsInSection, moveProgramBetweenSections],
  );

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40" style={{ zIndex: 40 }} onClick={onClose} />

      {/* Drawer */}
      <div
        className="fixed top-0 right-0 h-full w-full max-w-2xl bg-card shadow-xl flex flex-col"
        style={{ zIndex: 50 }}
        data-intercom-target="builder-v2-sections-drawer"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 style={{ fontWeight: 'var(--font-weight-semibold)' }}>
              <T kind="Sections & Courses" />
            </h2>
            <p className="text-muted-foreground" style={{ fontSize: 'var(--text-sm)' }}>
              <T kind="Drag to reorder, expand sections to view courses" />
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-md transition-colors"
            aria-label="Close"
            data-intercom-target="builder-v2-sections-drawer-close-button"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {hasUnlicensedPrograms && <DrawerWarningAlert onViewDetails={onViewLicenseDetails} />}

          {/* SectionsDrawer uses a separate DragDropContext because:
              1. It handles both SECTION reordering and PROGRAM reordering within the drawer
              2. Section reordering is independent of cross-section program moves in the main view
              3. The drawer uses different droppableId formats: "sections-list" for sections, "programs-{index}" for programs
              4. Drag behavior in the collapsed drawer differs from the expanded main view */}
          {sectionItems.length > 0 ? (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="sections-list" type="SECTION">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-3">
                    {sectionItems.map(({ section, index, key, programCount, isExpanded }) => (
                      <DraggableSection
                        key={key}
                        section={section}
                        index={index}
                        draggableId={`section-${key}`}
                        programCount={programCount}
                        isExpanded={isExpanded}
                        onToggleExpand={() => toggleSection(index)}
                        experienceMap={experienceMap}
                      />
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <p>
                <T kind="No sections added yet. Add a section to get started." />
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default observer(SectionsDrawer);
